import { Node } from '@tiptap/core'
import { mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react'

import {
  SupportedAudioFormats,
  SupportedVideoFormats,
} from '../../config/constants.js'
import { Video as VideoComponent } from './Video.js'

export interface VideoOptions {
  HTMLAttributes: Record<string, unknown>
}

export enum VideoSize {
  BestFit = 'best-fit',
  Original = 'original',
}

interface VideoAttr {
  id: string
  src: string
  type: string
  loading?: boolean
  size?: VideoSize
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      /**
       * Add an Video
       */
      setVideo: (options: VideoAttr) => ReturnType
      setVideoAttributes: (options: Partial<VideoAttr>) => ReturnType
    }
  }
}

export const Video = Node.create<VideoOptions>({
  name: 'video',

  inline: false,
  group: 'block',

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: undefined,
        rendered: false,
      },
      id: {
        default: undefined,
        rendered: false,
      },
      type: {
        default: undefined,
        rendered: false,
      },
      loading: {
        default: false,
        rendered: false,
      },
      size: {
        default: VideoSize.BestFit,
        rendered: false,
      },
    }
  },

  parseHTML: () => [
    {
      tag: 'video',
      getAttrs: dom => {
        if (typeof dom === 'string') {
          return {}
        }
        const element = dom as HTMLImageElement
        const source = element.querySelector('source')

        return {
          id: element.getAttribute('data-id'),
          type:
            element.getAttribute('data-file-type') || SupportedVideoFormats[0],
          src: element.getAttribute('src') || source?.getAttribute('src') || '',
          size: element.getAttribute('data-size'),
        }
      },
    },
    {
      tag: 'audio',
      getAttrs: dom => {
        if (typeof dom === 'string') {
          return {}
        }
        const element = dom as HTMLImageElement
        const source = element.querySelector('source')

        return {
          id: element.getAttribute('data-id'),
          type:
            element.getAttribute('data-file-type') || SupportedAudioFormats[0],
          src: element.getAttribute('src') || source?.getAttribute('src') || '',
          size: element.getAttribute('data-size'),
        }
      },
    },
  ],

  renderHTML({ node }) {
    const { src = '', id, type, size } = node.attrs

    const tag = type?.includes('audio') ? 'audio' : 'video'
    return [
      'figure',
      {
        'data-type': tag,
        'data-file-type': type,
        'data-id': id,
        'data-size': size,
      },
      [
        tag,
        mergeAttributes({
          'data-id': id,
          controls: true,
          src,
          size,
        }),
        `Your browser does not support the ${tag} element.`,
      ],
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoComponent)
  },

  addCommands() {
    return {
      setVideo:
        attrs =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
      setVideoAttributes:
        attributes =>
        ({ commands }) =>
          commands.updateAttributes(this.name, attributes),
    }
  },
})
